import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { WhoopDayData } from '@/hooks/useWhoopData';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Battery, Heart, Moon, Zap, TrendingUp } from 'lucide-react';

interface WhoopHistoryTableProps {
  data: WhoopDayData[];
  onRowClick?: (data: WhoopDayData) => void;
  onCompareClick?: (data: WhoopDayData) => void;
}

export default function WhoopHistoryTable({ data, onRowClick, onCompareClick }: WhoopHistoryTableProps) {
  const getRecoveryColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 67) return 'text-accent';
    if (score >= 34) return 'text-primary';
    return 'text-secondary';
  };

  const getSleepColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 85) return 'text-accent';
    if (score >= 70) return 'text-primary';
    return 'text-secondary';
  };

  const getStrainColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 14) return 'text-accent';
    if (score >= 10) return 'text-primary';
    return 'text-secondary';
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Historical Data</CardTitle>
        <CardDescription>Click any row to see detailed data for that day</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Battery className="h-4 w-4" />
                    Recovery
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Heart className="h-4 w-4" />
                    HRV
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Heart className="h-4 w-4" />
                    RHR
                  </div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Moon className="h-4 w-4" />
                    Sleep
                  </div>
                </TableHead>
                <TableHead className="text-center">Duration</TableHead>
                <TableHead className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Zap className="h-4 w-4" />
                    Strain
                  </div>
                </TableHead>
                <TableHead className="text-center">Calories</TableHead>
                {onCompareClick && <TableHead className="text-center">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow 
                  key={row.id}
                  className={cn(
                    'cursor-pointer hover:bg-secondary/10 transition-colors',
                    onRowClick && 'hover:bg-primary/10'
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  <TableCell className="font-medium">
                    {format(new Date(row.date), 'EEE, MMM d')}
                  </TableCell>
                  <TableCell className={cn('text-center font-semibold', getRecoveryColor(row.recovery_score))}>
                    {row.recovery_score ? `${Math.round(row.recovery_score)}%` : '--'}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.hrv_score ? Math.round(row.hrv_score) : '--'}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.resting_heart_rate ? Math.round(row.resting_heart_rate) : '--'}
                  </TableCell>
                  <TableCell className={cn('text-center font-semibold', getSleepColor(row.sleep_performance))}>
                    {row.sleep_performance ? `${Math.round(row.sleep_performance)}%` : '--'}
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">
                    {formatDuration(row.sleep_duration_minutes)}
                  </TableCell>
                  <TableCell className={cn('text-center font-semibold', getStrainColor(row.strain_score))}>
                    {row.strain_score ? row.strain_score.toFixed(1) : '--'}
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">
                    {row.calories_burned ? Math.round(row.calories_burned) : '--'}
                  </TableCell>
                  {onCompareClick && (
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCompareClick(row);
                        }}
                        className="h-7 px-2 text-xs"
                      >
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Compare
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
